import axiosInstance from "../api/Axiosinstance";
import { ENDPOINTS } from "../api/Endpoints";
import { ACCEPTED_IMAGE_TYPES, validateImageFile } from "../constants/imageUpload";

/**
 * Browser-side uploads.
 *
 * Files go straight from the browser to Cloudinary; the API only ever receives
 * the resulting `publicId` and verifies it against Cloudinary before storing
 * it. That keeps large files off our own server, gives us real progress
 * reporting, and lets a flaky connection be retried without re-posting the
 * file through the API.
 */

export const UPLOAD_PURPOSES = {
  PROFILE_PICTURE: "profile_picture",
  PROVIDER_LICENSE: "provider_license",
  PROVIDER_ID_DOCUMENT: "provider_id_document",
  VEHICLE_IMAGE: "vehicle_image",
  VEHICLE_DOCUMENT: "vehicle_document",
  STAYS_IMAGE: "stays_image",
  TRAVEL_ASSISTANT_PROFILE: "travel_assistant_profile",
  TRAVEL_ASSISTANT_GALLERY: "travel_assistant_gallery",
  PLATFORM_LOGO: "platform_logo",
};

/**
 * Per-purpose overrides of the shared image rules. The platform logo keeps the
 * wider format set and 2MB cap it has always accepted; everything else uses the
 * project-wide rules in constants/imageUpload. The API enforces its own limits
 * regardless — these only save a wasted upload.
 */
const PURPOSE_RULES = {
  [UPLOAD_PURPOSES.PLATFORM_LOGO]: {
    types: [...ACCEPTED_IMAGE_TYPES, "image/webp", "image/svg+xml", "image/gif"],
    maxBytes: 2 * 1024 * 1024,
  },
};

const MAX_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 800;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class UploadError extends Error {
  constructor(message, { retryable = false, status = null } = {}) {
    super(message);
    this.name = "UploadError";
    this.retryable = retryable;
    this.status = status;
  }
}

/** Reject files the API would reject anyway, before spending a round trip on them. */
export const validateUploadFile = (file, purpose) => {
  const override = PURPOSE_RULES[purpose];
  if (!override) return validateImageFile(file) || null;

  if (!file) return "Please select a file.";
  if (!override.types.includes(file.type)) {
    return "Please choose a valid image file.";
  }
  if (file.size > override.maxBytes) {
    return `File is too large. Maximum size is ${Math.round(
      override.maxBytes / (1024 * 1024)
    )}MB.`;
  }
  return null;
};

const requestSignature = async (purpose) => {
  const res = await axiosInstance.post(ENDPOINTS.UPLOADS.SIGNATURE, { purpose });
  const payload = res?.data?.data ?? res?.data;
  if (!payload?.signature || !payload?.uploadUrl) {
    throw new UploadError("Could not start the upload. Please try again.");
  }
  return payload;
};

/**
 * POST the file to Cloudinary. Uses XHR rather than fetch so upload progress
 * can be reported and the request can be aborted.
 */
const postToCloudinary = (file, signature, { onProgress, signal }) =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", signature.apiKey);
    form.append("timestamp", signature.timestamp);
    form.append("signature", signature.signature);
    form.append("folder", signature.folder);
    form.append("public_id", signature.publicId);
    form.append("tags", signature.tags);

    // Purposes that overwrite a single shared asset (the platform logo) sign
    // these too, so they have to be sent back verbatim.
    if (signature.overwrite) {
      form.append("overwrite", "true");
      form.append("invalidate", "true");
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", signature.uploadUrl, true);

    const abort = () => xhr.abort();
    if (signal) {
      if (signal.aborted) {
        reject(new UploadError("Upload cancelled."));
        return;
      }
      signal.addEventListener("abort", abort, { once: true });
    }

    const cleanup = () => signal?.removeEventListener("abort", abort);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      };
    }

    xhr.onload = () => {
      cleanup();
      let body = {};
      try {
        body = JSON.parse(xhr.responseText || "{}");
      } catch (e) {
        // fall through to the status checks below
      }

      if (xhr.status >= 200 && xhr.status < 300 && body.public_id) {
        resolve(body);
        return;
      }

      const message = body?.error?.message || "Upload failed. Please try again.";
      // 4xx means the request itself is wrong (bad signature, rejected file):
      // retrying it verbatim would fail identically, so don't.
      reject(new UploadError(message, { retryable: xhr.status >= 500, status: xhr.status }));
    };

    xhr.onerror = () => {
      cleanup();
      reject(new UploadError("Network error during upload.", { retryable: true }));
    };

    xhr.ontimeout = () => {
      cleanup();
      reject(new UploadError("Upload timed out.", { retryable: true }));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new UploadError("Upload cancelled."));
    };

    xhr.send(form);
  });

/**
 * Upload a file directly to Cloudinary, retrying transient failures.
 *
 * @param {File} file
 * @param {string} purpose one of UPLOAD_PURPOSES
 * @param {object} [options]
 * @param {(percent: number) => void} [options.onProgress]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{publicId: string, url: string, width: number, height: number}>}
 */
export const uploadFile = async (file, purpose, { onProgress, signal } = {}) => {
  const validationError = validateUploadFile(file, purpose);
  if (validationError) throw new UploadError(validationError);

  let lastError;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      // A fresh signature per attempt: they are short-lived, and each one is
      // bound to a distinct public_id so a partial upload is never reused.
      const signature = await requestSignature(purpose);
      const result = await postToCloudinary(file, signature, { onProgress, signal });

      return {
        publicId: result.public_id,
        url: result.secure_url,
        width: result.width,
        height: result.height,
      };
    } catch (error) {
      lastError = error;

      const isRetryable = error instanceof UploadError ? error.retryable : true;
      if (!isRetryable || signal?.aborted || attempt === MAX_ATTEMPTS) break;

      onProgress?.(0);
      await wait(BASE_RETRY_DELAY_MS * 2 ** (attempt - 1));
    }
  }

  throw lastError;
};

/** How many files of a batch are in flight at once. */
const BATCH_CONCURRENCY = 3;

/**
 * Upload several files for the same purpose, preserving input order.
 *
 * `onProgress` reports across the whole batch rather than per file, which is
 * what a gallery upload wants to show. One failure rejects the batch — a
 * partially uploaded gallery is worse than a clear error.
 *
 * @param {File[]} files
 * @param {string} purpose one of UPLOAD_PURPOSES
 * @returns {Promise<Array<{publicId: string, url: string}>>}
 */
export const uploadFiles = async (files, purpose, { onProgress, signal } = {}) => {
  const list = Array.from(files || []);
  if (!list.length) return [];

  const invalid = list.map((file) => validateUploadFile(file, purpose)).find(Boolean);
  if (invalid) throw new UploadError(invalid);

  const results = new Array(list.length);
  const percentages = new Array(list.length).fill(0);

  const reportTotal = () => {
    if (!onProgress) return;
    const total = percentages.reduce((sum, value) => sum + value, 0);
    onProgress(Math.round(total / list.length));
  };

  let cursor = 0;
  const worker = async () => {
    while (cursor < list.length) {
      const index = cursor;
      cursor += 1;

      results[index] = await uploadFile(list[index], purpose, {
        signal,
        onProgress: (percent) => {
          percentages[index] = percent;
          reportTotal();
        },
      });

      percentages[index] = 100;
      reportTotal();
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(BATCH_CONCURRENCY, list.length) }, worker)
  );

  return results;
};

const directUpload = { uploadFile, uploadFiles, validateUploadFile, UPLOAD_PURPOSES };

export default directUpload;
